import type { Attribute } from '@strapi/strapi';

import type { AudienceEntry } from '@tmh-website/services/audience.service';
import type { Component, DynamicZoneComponent } from '@tmh-website/components/blocks.types';
import type { HasGtmId, HasMeasurementId } from '@tmh-website/components/global/GoogleAnalytics';
import type { HeaderThemeColor } from '@tmh-website/components/global/Header';
import type { Job } from '@tmh-website/services/job.service';
import type { Product } from '@tmh-website/services/magento/graphql.service';
import type { Theme } from '@tmh-website/services/electricity-tariff/utils/types';
import { isComponent } from '@tmh-website/components/blocks.types';

import type { Audience, Locale } from '../const';
import type { ISODate } from '../types';
import { LocalePrefix, LocaleToAudienceMap, PageType, getFavoredAudience } from '../const';

export interface GlobalProps {
  global: Omit<GlobalData, 'audience' | 'setAudience' | 'audienceIsLoaded'>;
  page: AnyPage;
  pageType?: PageType;
}

export interface ResponseSingleType<T> {
  data: T | null;
  meta?: Record<string, string>;
}

export interface ResponseCollectionType<T> {
  data: T[];
  meta?: { pagination?: Pagination };
}

export interface Pagination {
  page: number;
  pageSize: number;
  pageCount: number;
  total: number;
}

export interface PaginationInput {
  page: number;
  pageSize: number;
}

export interface GlobalData
  extends Pick<
    Site,
    | 'footerCertificates'
    | 'footerContactSectionHeadline'
    | 'footerContactSectionLink'
    | 'footerContactSectionText'
    | 'footerLegalLinks'
    | 'footerLinkGroups'
    | 'footerLogisticPartners'
    | 'footerForm'
    | 'footerPartners'
    | 'footerPaymentPartners'
    | 'footerSpecialLink'
    | 'gtmId'
    | 'headerLink'
    | 'homepageLinks'
    | 'isHeaderAccountButtonVisible'
    | 'isHeaderCartButtonVisible'
    | 'isHeaderFavoritesButtonVisible'
    | 'isHeaderHamburgerButtonVisible'
    | 'isHeaderSearchButtonVisible'
    | 'measurementId'
    | 'menuQuickLinks'
    | 'metaDescription'
    | 'metaImage'
    | 'metaTitle'
    | 'navigation'
    | 'navigationAudiencesButton'
    | 'navigationHighlights'
    | 'socialLinks'
  > {
  audience: Audience | null;
  audiences?: AudienceEntry[];
  audienceIsLoaded: boolean;
  customLogo?: string;
  footerTopLogo?: Attribute.GetValues<'api::electricity-tariff.electricity-tariff-theme'>['footerTopLogo']; // Only used in ET
  headerTheme?: HeaderThemeColor;
  isPreview?: { exp: number }; // Only used in ET
  robots?: string; // Only used in ET
  setAudience: (newAudience: Audience) => void; // When the page is viewed in the CMS preview mode
}

export interface NavigationPageLinkGroup extends Component<'navigation.page-link-group'> {
  children?: (Attribute.GetValues<'navigation.page-link'> & { image?: Image })[];
}

export interface NavigationPageLink extends Component<'navigation.page-link'> {
  image?: Image;
}

export type NavigationElement = NavigationPageLink | NavigationPageLinkGroup;

export type NavigationHighlightLinkGroup = Attribute.GetValues<'navigation.highlight-link-group'>;

export interface Site extends Attribute.GetValues<'api::site.site'>, Partial<HasGtmId>, Partial<HasMeasurementId> {
  metaImage?: Image;
  navigation?: NavigationElement[];
  socialLinks?: (Attribute.GetValues<'navigation.social-link'> & { icon?: IconSvgObject })[];
}

export interface ContactSupport
  extends Omit<Attribute.GetValues<'api::site.contact-support'>, 'contactSupportCategories'> {
  contactSupportCategories: (Omit<
    Attribute.GetValues<'site.contact-support-faq-category'>,
    'contactSupportFaqEntries' | 'icon'
  > & {
    icon?: IconSvgObject | string;
    contactSupportFaqEntries: (Omit<Attribute.GetValues<'site.contact-support-faq-entry'>, 'audiences'> & {
      audiences: Audience[];
    })[];
  })[];
}

export type ElectricityTariffTheme = Theme & Site;

export interface ImageFormat {
  ext: string;
  hash: string;
  height: number;
  mime: string;
  name: string;
  path: null;
  size: number;
  url: string;
  width: number;
}

export interface Image {
  id?: number;
  alternativeText?: string;
  ext: string;
  hash: string;
  width: number;
  height: number;
  url: string;
  type: 'image';
}

/**
 * Check if the object returned from Strapi is an image.
 */
export function isStrapiImage(obj: any): obj is Image {
  return obj?.ext !== undefined && obj?.hash !== undefined;
}

export interface Video {
  alternativeText: string;
  caption: string;
  mime: string;
  url: string;
  type: 'video';
}

/**
 * Check if the object returned from Strapi is a video.
 */
export function isStrapiVideo(obj: any): obj is Video {
  return obj?.ext !== undefined && obj?.hash !== undefined && obj?.mime.startsWith('video');
}

/**
 * This is used to render library icons in our icon component.
 * We split the icon component into two parts so that the parent element can be a svg with dangerouslySetInnerHTML.
 */
export interface IconSvgObject {
  attributes?: Record<string, string | undefined>;
  children?: string;
}

export interface Person extends Attribute.GetValues<'api::person.person'> {
  image?: Image;
  type: 'Internal' | 'External';
}

export interface ElectricityTariffPerson
  extends Attribute.GetValues<'api::electricity-tariff.electricity-tariff-person'> {
  image?: Image;
  type: 'Internal' | 'External';
}

export interface EnergyPerson extends Attribute.GetValues<'api::energy.energy-person'> {
  image?: Image;
  type: 'Internal' | 'External';
}

export function isPerson(obj: any): obj is Person {
  return (
    (obj as Person)?.name !== undefined &&
    ((obj as Person)?.type !== 'Internal' || (obj as Person)?.type !== 'External')
  );
}

export function isElectricityTariffPerson(obj: any): obj is ElectricityTariffPerson {
  return (
    (obj as ElectricityTariffPerson)?.name !== undefined &&
    ((obj as ElectricityTariffPerson)?.type !== 'Internal' || (obj as ElectricityTariffPerson)?.type !== 'External')
  );
}

export function isEnergyPerson(obj: any): obj is EnergyPerson {
  return (
    (obj as EnergyPerson)?.name !== undefined &&
    ((obj as EnergyPerson)?.type !== 'Internal' || (obj as EnergyPerson)?.type !== 'External')
  );
}

export type PartnersLogoWidth = 'xSmall' | 'small' | 'medium' | 'large' | 'xLarge';

export interface Partner extends Omit<Attribute.GetValues<'api::partner.partner'>, 'locale'> {
  logo: Image;
  logoWidth: PartnersLogoWidth;
  name: string;
  url?: string;
}

export type Tag =
  | Attribute.GetValues<'api::energy.energy-knowledge-center-tag'>
  | Attribute.GetValues<'api::energy.energy-news-article-tag'>
  | Attribute.GetValues<'api::energy.energy-reference-tag'>
  | Attribute.GetValues<'api::knowledge-center.knowledge-center-tag'>
  | Attribute.GetValues<'api::news.news-tag'>
  | Attribute.GetValues<'api::reference.reference-tag'>
  | Attribute.GetValues<'api::styleguide.styleguide-tag'>;

export type AnyPage =
  | ChargepilotProductUpdateArticlePage
  | CarDetailPage
  | ElectricityTariffBlogArticlePage
  | ElectricityTariffPage
  | EnergyKnowledgeCenterArticlePage
  | EnergyKnowledgeCenterWebinarPage
  | EnergyKnowledgeCenterWhitepaperPage
  | EnergyNewsArticlePage
  | EnergyPage
  | EnergyReferenceArticlePage
  | JobDetailPage
  | KnowledgeCenterArticlePage
  | KnowledgeCenterWebinarPage
  | KnowledgeCenterWhitepaperPage
  | NewsArticlePage
  | Page
  | ReferenceArticlePage
  | SearchDetailPage
  | ShopCategoryPage
  | StyleguideArticlePage;

export interface Page extends Omit<Attribute.GetValues<'api::page.page'>, 'audiences'> {
  audiences: Audience[];
}

/**
 * Get audience based on locale and page.
 * Locale is mandatory because we have no `page` information when in the website Magento header context
 */
export function getDefaultAudience(locale: LocalePrefix, page?: AnyPage) {
  const audiences = page ? page.audiences : LocaleToAudienceMap[locale];
  return getFavoredAudience(audiences);
}

export type ArticlePage =
  | ChargepilotProductUpdateArticlePage
  | ElectricityTariffBlogArticlePage
  | EnergyKnowledgeCenterArticlePage
  | EnergyKnowledgeCenterWebinarPage
  | EnergyKnowledgeCenterWhitepaperPage
  | EnergyNewsArticlePage
  | EnergyReferenceArticlePage
  | KnowledgeCenterArticlePage
  | KnowledgeCenterWebinarPage
  | KnowledgeCenterWhitepaperPage
  | NewsArticlePage
  | ReferenceArticlePage
  | StyleguideArticlePage;

export type ArticlePageAsEntry =
  | PageAsEntry<ElectricityTariffBlogArticlePage>
  | PageAsEntry<EnergyKnowledgeCenterArticlePage>
  | PageAsEntry<EnergyKnowledgeCenterWebinarPage>
  | PageAsEntry<EnergyKnowledgeCenterWhitepaperPage>
  | PageAsEntry<EnergyNewsArticlePage>
  | PageAsEntry<EnergyReferenceArticlePage>
  | PageAsEntry<KnowledgeCenterArticlePage>
  | PageAsEntry<KnowledgeCenterWebinarPage>
  | PageAsEntry<KnowledgeCenterWhitepaperPage>
  | PageAsEntry<NewsArticlePage>
  | PageAsEntry<ReferenceArticlePage>
  | PageAsEntry<StyleguideArticlePage>;

// Unnecessary attributes for articleEntries have been removed here
export type PageAsEntry<T> = Omit<
  T,
  | 'aliasLocale'
  | 'canonical'
  | 'content'
  | 'formParameters'
  | 'heroImage'
  | 'localizations'
  | 'metaDescription'
  | 'metaRobots'
  | 'metaTitle'
  | 'structuredData'
  | 'videoUrl'
>;

export interface KnowledgeCenterArticlePage
  extends Omit<
    Attribute.GetValues<'api::knowledge-center.knowledge-center-article'>,
    'author' | 'audiences' | 'postDate' | 'editDate'
  > {
  variant: ArticleVariant;
  author: Person;
  image: Image;
  audiences: Audience[];
  postDate: string;
  editDate: string;
  relatedArticles?: ArticlePageAsEntry[];
}

export interface EnergyKnowledgeCenterArticlePage
  extends Omit<
    Attribute.GetValues<'api::energy.energy-knowledge-center-article'>,
    'author' | 'audiences' | 'postDate' | 'editDate'
  > {
  variant: ArticleVariant;
  author: Person;
  image: Image;
  audiences: Audience[];
  postDate: string;
  editDate: string;
  relatedArticles?: ArticlePageAsEntry[];
}

export interface KnowledgeCenterWebinarPage
  extends Omit<
    Attribute.GetValues<'api::knowledge-center.knowledge-center-webinar'>,
    'startDate' | 'endDate' | 'audiences'
  > {
  heroImage?: Image;
  startDate: ISODate;
  endDate: ISODate;
  audiences: Audience[];
  relatedArticles?: ArticlePageAsEntry[];
}

export interface EnergyKnowledgeCenterWebinarPage
  extends Omit<
    Attribute.GetValues<'api::energy.energy-knowledge-center-webinar'>,
    'startDate' | 'endDate' | 'audiences'
  > {
  heroImage?: Image;
  startDate: ISODate;
  endDate: ISODate;
  audiences: Audience[];
  relatedArticles?: ArticlePageAsEntry[];
}

export interface SearchDetailPage
  extends Omit<Attribute.GetValues<'api::page.page'>, 'headerTheme' | 'audiences' | 'content'> {
  audiences: Audience[];
  searchContentCards: (
    | DynamicZoneComponent<'shop.search-content-card-big'>
    | DynamicZoneComponent<'shop.search-content-card-small'>
  )[];
}

export interface KnowledgeCenterWhitepaperPage
  extends Omit<Attribute.GetValues<'api::knowledge-center.knowledge-center-whitepaper'>, 'audiences'> {
  heroImage?: Image;
  audiences: Audience[];
}

export interface EnergyKnowledgeCenterWhitepaperPage
  extends Omit<Attribute.GetValues<'api::energy.energy-knowledge-center-whitepaper'>, 'audiences'> {
  heroImage?: Image;
  audiences: Audience[];
}

export interface NewsArticlePage
  extends Omit<Attribute.GetValues<'api::news.news-article'>, 'audiences' | 'postDate' | 'editDate'> {
  audiences: Audience[];
  postDate: string;
  editDate: string;
  relatedArticles?: ArticlePageAsEntry[];
}

export interface EnergyNewsArticlePage
  extends Omit<Attribute.GetValues<'api::energy.energy-news-article'>, 'audiences' | 'postDate' | 'editDate'> {
  audiences: Audience[];
  postDate: string;
  editDate: string;
  relatedArticles?: ArticlePageAsEntry[];
}

export type ArticleVariant = 'FullArticle' | 'CardOnly';

export const DEFAULT_ARTICLE_VARIANT: ArticleVariant = 'FullArticle';

export interface ReferenceArticlePage
  extends Omit<Attribute.GetValues<'api::reference.reference-article'>, 'audiences' | 'partner'> {
  variant: ArticleVariant;
  audiences: Audience[];
  partner: Partner;
  relatedArticles?: ArticlePageAsEntry[];
}

export interface EnergyReferenceArticlePage
  extends Omit<Attribute.GetValues<'api::energy.energy-reference-article'>, 'audiences' | 'partner'> {
  variant: ArticleVariant;
  audiences: Audience[];
  partner: Partner;
  relatedArticles?: ArticlePageAsEntry[];
}

export interface JobDetailPage
  extends Omit<Attribute.GetValues<'api::page.page'>, 'headerTheme' | 'audiences' | 'content'> {
  job: Job;
  audiences: Audience[];
}
export interface StyleguideArticlePage
  extends Omit<
    Attribute.GetValues<'api::knowledge-center.knowledge-center-article'>,
    'author' | 'audiences' | 'postDate' | 'editDate'
  > {
  author: Person;
  image: Image;
  audiences: Audience[];
  postDate: string;
  editDate: string;
  relatedArticles?: ArticlePageAsEntry[];
}

export interface ChargepilotProductUpdateArticlePage
  extends Omit<
    Attribute.GetValues<'api::chargepilot.chargepilot-product-update-article'>,
    'audiences' | 'author' | 'editDate' | 'postDate'
  > {
  audiences: Audience[];
  author: Person;
  editDate: string;
  image: Image;
  postDate: string;
  relatedArticles?: ArticlePageAsEntry[];
}

// We need to omit "createdAt" and "updatedAt" since those types are wrongly optional in the strapi
export interface CarDetailPage extends Attribute.GetValues<'api::car.car-model'> {
  /**
   * Static and automatically generated by Next.js. Cannot be changed via the CMS.
   */
  title: string;
  /**
   * Static and automatically generated by Next.js. Cannot be changed via the CMS.
   */
  metaTitle: string;
  /**
   * Static and automatically generated by Next.js. Cannot be changed via the CMS.
   */
  audiences: Audience[];
  /**
   * Static and automatically generated by Next.js. Cannot be changed via the CMS.
   */
  metaDescription: string;
  /**
   * Static and automatically generated by Next.js. Cannot be changed via the CMS.
   */
  metaImage?: Image;
  /**
   * Static and automatically generated by Next.js. Cannot be changed via the CMS.
   */
  metaRobots: string;
  /**
   * Static and automatically generated by Next.js. Cannot be changed via the CMS.
   */
  structuredData: null;
  /**
   * Static and automatically generated by Next.js. Cannot be changed via the CMS.
   */
  canonical: undefined;
  /**
   * This is a virtual property that is not coming from the CMS.
   * Instead, it's populated by Next.js by loading the `car-detail-template` page, manipulating it and assigning it to this property for later use.
   */
  template: CarDetailTemplate;
  /**
   * This value will be field from the CMS - electricity tariff theme - kickback factor with smart meter (needed for the CalculatorChargingCost)
   */
  kickbackFactorWithSmartMeter: number;
}

export interface CarDetailTemplate extends Attribute.GetValues<'api::car.car-detail-template'> {
  highlightedProductSliderEntries: (Attribute.GetValues<'content.highlighted-product-slider-entry'> & {
    product?: Product;
  })[];
}

export interface ElectricityTariffPage extends Attribute.GetValues<'api::electricity-tariff.electricity-tariff-page'> {
  audiences: Audience[];
}

export interface EnergyPage extends Omit<Attribute.GetValues<'api::energy.energy-page'>, 'audiences'> {
  audiences: Audience[];
}

export interface ElectricityTariffBlogArticlePage
  extends Omit<
    Attribute.GetValues<'api::electricity-tariff.electricity-tariff-blog-article'>,
    'author' | 'postDate' | 'editDate' | 'audiences'
  > {
  author: ElectricityTariffPerson;
  image: Image;
  audiences: Audience[];
  postDate: string;
  editDate: string;
  relatedArticles?: ArticlePageAsEntry[];
}

export interface ShopCategoryPage
  extends Omit<Attribute.GetValues<'api::shop-category-page.shop-category-page'>, 'audiences'> {
  audiences: Audience[];
}

export enum RedirectType {
  Temporary = 'Temporary',
  Permanent = 'Permanent',
  Gone = 'Gone',
}

// This has to be kept in sync with the LinkField custom field and Canonical custom field
export enum LinkType {
  Car = 'Car',
  ChargepilotProductUpdateArticle = 'ChargepilotProductUpdateArticle',
  ElectricityTariffBlogArticle = 'ElectricityTariffBlogArticle',
  ElectricityTariffPage = 'ElectricityTariffPage',
  EnergyKnowledgeCenterArticle = 'EnergyKnowledgeCenterArticle',
  EnergyKnowledgeCenterWebinar = 'EnergyKnowledgeCenterWebinar',
  EnergyKnowledgeCenterWhitepaper = 'EnergyKnowledgeCenterWhitepaper',
  EnergyNewsArticle = 'EnergyNewsArticle',
  EnergyPage = 'EnergyPage',
  EnergyReferenceArticle = 'EnergyReferenceArticle',
  KnowledgeCenterArticle = 'KnowledgeCenterArticle',
  KnowledgeCenterWebinar = 'KnowledgeCenterWebinar',
  KnowledgeCenterWhitepaper = 'KnowledgeCenterWhitepaper',
  NewsArticle = 'NewsArticle',
  Page = 'Page',
  ReferenceArticle = 'ReferenceArticle',
  ShopCategoryPage = 'ShopCategoryPage',
  StyleguideArticle = 'StyleguideArticle',
  StyleguidePage = 'StyleguidePage',
  URL = 'URL',
}

export enum LinkTypeWebsite {
  ChargepilotProductUpdateArticle = 'ChargepilotProductUpdateArticle',
  KnowledgeCenterArticle = 'KnowledgeCenterArticle',
  KnowledgeCenterWebinar = 'KnowledgeCenterWebinar',
  KnowledgeCenterWhitepaper = 'KnowledgeCenterWhitepaper',
  NewsArticle = 'NewsArticle',
  Page = 'Page',
  ReferenceArticle = 'ReferenceArticle',
  ShopCategoryPage = 'ShopCategoryPage',
}

export enum LinkTypeElectricityTariff {
  ElectricityTariffBlogArticle = 'ElectricityTariffBlogArticle',
  ElectricityTariffPage = 'ElectricityTariffPage',
}

export enum LinkTypeStyleguide {
  StyleguideArticle = 'StyleguideArticle',
  StyleguidePage = 'StyleguidePage',
}

export enum LinkTypeEnergy {
  EnergyKnowledgeCenterArticle = 'EnergyKnowledgeCenterArticle',
  EnergyKnowledgeCenterWebinar = 'EnergyKnowledgeCenterWebinar',
  EnergyKnowledgeCenterWhitepaper = 'EnergyKnowledgeCenterWhitepaper',
  EnergyNewsArticle = 'EnergyNewsArticle',
  EnergyPage = 'EnergyPage',
  EnergyReferenceArticle = 'EnergyReferenceArticle',
}

// This has to be kept in sync with the LinkField custom field
export interface LinkField {
  _type: 'link-field';
  type: LinkType;
  label: string;
  value: string;
  anchor?: string;
  /**
   * It's important that this property is NOT called `audiences` as it would be transformed
   * by the `customParseJson` function in `cms.service.ts` and therefore removed.
   */
  attributes?: { name: string; value: string }[];
  relevantAudiences?: Audience[];
}

// This has to be kept in sync with the CanonicalField custom field
export interface CanonicalField {
  _type: 'canonical-field';
  category: string;
  path: string;
  type: LinkTypeWebsite | LinkTypeElectricityTariff | LinkTypeStyleguide | LinkTypeEnergy;
  value: string;
}

/**
 * Validates that this is a LinkField JSON object and has a valid link value.
 */
export function isValidLinkField(linkField: unknown): linkField is LinkField {
  return (
    (linkField as LinkField)?._type === 'link-field' &&
    (!!(linkField as LinkField)?.value || !!(linkField as LinkField)?.anchor)
  );
}

export const isLinkField = (linkField: unknown): linkField is LinkField =>
  (linkField as LinkField)?._type === 'link-field';

/**
 * Validates that this is a CanonicalField JSON object and has a valid link value.
 */
export function isValidCanonicalField(canonicalField: unknown): canonicalField is CanonicalField {
  return (canonicalField as CanonicalField)?._type === 'canonical-field' && !!(canonicalField as CanonicalField)?.value;
}

export const redirectToStatusMap: { [key in RedirectType]: number } = {
  [RedirectType.Temporary]: 307,
  [RedirectType.Permanent]: 301,
  [RedirectType.Gone]: 410,
};

export type Redirect = Attribute.GetValues<'api::redirect.redirect'>;

export function isNavigationPageLinkGroup(navEl: NavigationElement): navEl is NavigationPageLinkGroup {
  return (navEl as NavigationPageLinkGroup).children !== undefined;
}

export type ArticlesVariant = 'articles' | 'articlesMix';

export enum ArticleGroup {
  Chargepilot = 'Chargepilot',
  ElectricityTariffBlogArticle = 'ElectricityTariffBlogArticle',
  EnergyKnowledgeCenterArticle = 'EnergyKnowledgeCenterArticle',
  EnergyKnowledgeCenterWebinar = 'EnergyKnowledgeCenterWebinar',
  EnergyKnowledgeCenterWhitepaper = 'EnergyKnowledgeCenterWhitepaper',
  EnergyNewsArticle = 'EnergyNewsArticle',
  EnergyReferenceArticle = 'EnergyReferenceArticle',
  KnowledgeCenter = 'KnowledgeCenter',
  News = 'News',
  Reference = 'Reference',
  Styleguide = 'Styleguide',
}

export enum ArticleType {
  ChargepilotProductUpdateArticle = PageType.ChargepilotProductUpdateArticle,
  ElectricityTariffBlogArticle = PageType.ElectricityTariffBlogArticle,
  EnergyKnowledgeCenterArticle = PageType.EnergyKnowledgeCenterArticle,
  EnergyKnowledgeCenterWebinar = PageType.EnergyKnowledgeCenterWebinar,
  EnergyKnowledgeCenterWhitepaper = PageType.EnergyKnowledgeCenterWhitepaper,
  EnergyNewsArticle = PageType.EnergyNewsArticle,
  EnergyReferenceArticle = PageType.EnergyReferenceArticle,
  KnowledgeCenterArticle = PageType.KnowledgeCenterArticle,
  KnowledgeCenterWebinar = PageType.KnowledgeCenterWebinar,
  KnowledgeCenterWhitepaper = PageType.KnowledgeCenterWhitepaper,
  NewsArticle = PageType.NewsArticle,
  ReferenceArticle = PageType.ReferenceArticle,
  StyleguideArticle = PageType.StyleguideArticle,
}

export const ArticleTypeToPublishDateFieldMap: { [key in ArticleType]: string } = {
  [ArticleType.ChargepilotProductUpdateArticle]: 'editDate',
  [ArticleType.ElectricityTariffBlogArticle]: 'editDate',
  [ArticleType.EnergyKnowledgeCenterArticle]: 'editDate',
  [ArticleType.EnergyKnowledgeCenterWebinar]: 'startDate',
  [ArticleType.EnergyKnowledgeCenterWhitepaper]: 'publishedAt',
  [ArticleType.EnergyNewsArticle]: 'editDate',
  [ArticleType.EnergyReferenceArticle]: 'publishedAt',
  [ArticleType.KnowledgeCenterArticle]: 'editDate',
  [ArticleType.KnowledgeCenterWebinar]: 'startDate',
  [ArticleType.KnowledgeCenterWhitepaper]: 'publishedAt',
  [ArticleType.NewsArticle]: 'editDate',
  [ArticleType.ReferenceArticle]: 'publishedAt',
  [ArticleType.StyleguideArticle]: 'publishedAt',
};

export const ArticleGroupToTypeMap: Record<ArticleGroup, ArticleType[]> = {
  [ArticleGroup.Chargepilot]: [ArticleType.ChargepilotProductUpdateArticle],
  [ArticleGroup.ElectricityTariffBlogArticle]: [ArticleType.ElectricityTariffBlogArticle],
  [ArticleGroup.EnergyKnowledgeCenterArticle]: [ArticleType.EnergyKnowledgeCenterArticle],
  [ArticleGroup.EnergyKnowledgeCenterWebinar]: [ArticleType.EnergyKnowledgeCenterWebinar],
  [ArticleGroup.EnergyKnowledgeCenterWhitepaper]: [ArticleType.EnergyKnowledgeCenterWhitepaper],
  [ArticleGroup.EnergyNewsArticle]: [ArticleType.EnergyNewsArticle],
  [ArticleGroup.EnergyReferenceArticle]: [ArticleType.EnergyReferenceArticle],
  [ArticleGroup.KnowledgeCenter]: [
    ArticleType.KnowledgeCenterArticle,
    ArticleType.KnowledgeCenterWebinar,
    ArticleType.KnowledgeCenterWhitepaper,
  ],
  [ArticleGroup.News]: [ArticleType.NewsArticle],
  [ArticleGroup.Reference]: [ArticleType.ReferenceArticle],
  [ArticleGroup.Styleguide]: [ArticleType.StyleguideArticle],
};

export const ArticleTypeToGroupMap: Record<ArticleType, ArticleGroup> = {
  [ArticleType.ChargepilotProductUpdateArticle]: ArticleGroup.Chargepilot,
  [ArticleType.ElectricityTariffBlogArticle]: ArticleGroup.ElectricityTariffBlogArticle,
  [ArticleType.EnergyKnowledgeCenterArticle]: ArticleGroup.EnergyKnowledgeCenterArticle,
  [ArticleType.EnergyKnowledgeCenterWebinar]: ArticleGroup.EnergyKnowledgeCenterWebinar,
  [ArticleType.EnergyKnowledgeCenterWhitepaper]: ArticleGroup.EnergyKnowledgeCenterWhitepaper,
  [ArticleType.EnergyNewsArticle]: ArticleGroup.EnergyNewsArticle,
  [ArticleType.EnergyReferenceArticle]: ArticleGroup.EnergyReferenceArticle,
  [ArticleType.KnowledgeCenterArticle]: ArticleGroup.KnowledgeCenter,
  [ArticleType.KnowledgeCenterWebinar]: ArticleGroup.KnowledgeCenter,
  [ArticleType.KnowledgeCenterWhitepaper]: ArticleGroup.KnowledgeCenter,
  [ArticleType.NewsArticle]: ArticleGroup.News,
  [ArticleType.ReferenceArticle]: ArticleGroup.Reference,
  [ArticleType.StyleguideArticle]: ArticleGroup.Styleguide,
};

export const getGroupFromType = (articleType: ArticleType): ArticleGroup | undefined => {
  return ArticleTypeToGroupMap[articleType];
};

export const ArticleGroupToTagsEndpointMap: { [key in ArticleGroup]: string } = {
  [ArticleGroup.Chargepilot]: 'chargepilot-tags',
  [ArticleGroup.ElectricityTariffBlogArticle]: 'knowledge-center-tags',
  [ArticleGroup.EnergyKnowledgeCenterArticle]: 'energy-knowledge-center-tags',
  [ArticleGroup.EnergyKnowledgeCenterWebinar]: 'energy-knowledge-center-tags',
  [ArticleGroup.EnergyKnowledgeCenterWhitepaper]: 'energy-knowledge-center-tags',
  [ArticleGroup.EnergyNewsArticle]: 'energy-news-article-tags',
  [ArticleGroup.EnergyReferenceArticle]: 'energy-reference-tags',
  [ArticleGroup.KnowledgeCenter]: 'knowledge-center-tags',
  [ArticleGroup.News]: 'news-tags',
  [ArticleGroup.Reference]: 'reference-tags',
  [ArticleGroup.Styleguide]: 'styleguide-tags',
};

export const LocalePrefixToURLPathsMap: {
  [key in LocalePrefix]: {
    article: string;
    car: string;
    career: string;
    chargepilot: string;
    chargepilotProductUpdateArticle: string;
    contact: string;
    electricityTariffBlogArticle: string;
    energyNews: string;
    energyReferences: string;
    knowledgeCenter: string;
    energyKnowledgeCenter: string;
    news: string;
    ourCompany: string;
    references: string;
    search: string;
    styleguideArticle: string;
    webinar: string;
    whitepaper: string;
  };
} = {
  [LocalePrefix.En]: {
    article: 'article',
    car: 'electric-cars',
    career: 'career',
    chargepilot: 'chargepilot',
    chargepilotProductUpdateArticle: 'chargepilot/product-updates',
    contact: 'contact',
    electricityTariffBlogArticle: 'blog',
    energyNews: 'news',
    energyReferences: 'references',
    knowledgeCenter: 'knowledge-center',
    energyKnowledgeCenter: 'knowledge-center',
    news: 'newsroom',
    ourCompany: 'our-company',
    references: 'references',
    search: 'search',
    styleguideArticle: 'article',
    webinar: 'webinar',
    whitepaper: 'whitepaper',
  },
  [LocalePrefix.De]: {
    article: 'artikel',
    car: 'elektroautos',
    career: 'karriere',
    chargepilot: 'chargepilot',
    chargepilotProductUpdateArticle: 'chargepilot/product-updates',
    contact: 'kontakt',
    electricityTariffBlogArticle: 'blog',
    energyNews: 'news',
    energyReferences: 'references',
    knowledgeCenter: 'knowledge-center',
    energyKnowledgeCenter: 'knowledge-center',
    news: 'presse',
    ourCompany: 'unser-unternehmen',
    references: 'referenzen',
    search: 'suchergebnisse',
    styleguideArticle: 'artikel',
    webinar: 'webinar',
    whitepaper: 'whitepaper',
  },
  [LocalePrefix.DeCH]: {
    article: 'artikel',
    car: 'elektroautos',
    career: 'karriere',
    chargepilot: 'chargepilot',
    chargepilotProductUpdateArticle: 'chargepilot/product-updates',
    contact: 'kontakt',
    electricityTariffBlogArticle: 'blog',
    energyNews: 'news',
    energyReferences: 'references',
    knowledgeCenter: 'knowledge-center',
    energyKnowledgeCenter: 'knowledge-center',
    news: 'presse',
    ourCompany: 'unser-unternehmen',
    references: 'referenzen',
    search: 'suchergebnisse',
    styleguideArticle: 'artikel',
    webinar: 'webinar',
    whitepaper: 'whitepaper',
  },
  [LocalePrefix.EnUS]: {
    article: 'article',
    car: 'electric-cars',
    career: 'career',
    chargepilot: 'chargepilot',
    chargepilotProductUpdateArticle: 'chargepilot/product-updates',
    contact: 'contact',
    electricityTariffBlogArticle: 'blog',
    energyNews: 'news',
    energyReferences: 'references',
    knowledgeCenter: 'knowledge-center',
    energyKnowledgeCenter: 'knowledge-center',
    news: 'newsroom',
    ourCompany: 'our-company',
    references: 'references',
    search: 'search',
    styleguideArticle: 'article',
    webinar: 'webinar',
    whitepaper: 'whitepaper',
  },
  [LocalePrefix.FrCH]: {
    article: '???',
    car: '???',
    career: '???',
    chargepilot: '???',
    chargepilotProductUpdateArticle: '???',
    contact: '???',
    electricityTariffBlogArticle: '???',
    energyNews: '???',
    energyReferences: '???',
    knowledgeCenter: '???',
    energyKnowledgeCenter: '???',
    news: '???',
    ourCompany: '???',
    references: '???',
    search: '???',
    styleguideArticle: '???',
    webinar: '???',
    whitepaper: '???',
  },
  [LocalePrefix.Fr]: {
    article: 'article',
    car: 'voiture',
    career: 'carriere',
    chargepilot: 'chargepilot',
    chargepilotProductUpdateArticle: 'chargepilot/product-updates',
    contact: 'contact',
    electricityTariffBlogArticle: 'blog',
    energyNews: 'news',
    energyReferences: 'references',
    knowledgeCenter: 'knowledge-center',
    energyKnowledgeCenter: 'knowledge-center',
    news: 'actualites',
    ourCompany: 'notre-entreprise',
    references: 'references',
    search: 'recherche',
    styleguideArticle: 'article',
    webinar: 'webinar',
    whitepaper: 'whitepaper',
  },
};

export interface IconApiResponse {
  data?: IconSvgObject;
  message?: string;
  success: boolean;
}

export interface SitemapApiResponse {
  data?: string;
  message?: string;
  success: boolean;
}

export type Shop = Attribute.GetValues<'api::shop.shop'>;

export function isShopContentCard(
  card: any
): card is Component<'shop.content-card-small'> | Component<'shop.content-card-big'> {
  return isComponent(card, 'shop.content-card-small') || isComponent(card, 'shop.content-card-big');
}

export type PathMap = Record<PageType, Record<number, PathMapEntry>>;

export interface PathMapEntry {
  audiences?: Audience[];
  isPublished: boolean;
  locale: Locale;
  path: string;
  theme?: { host: string };
}

type ComponentWithVisibleFlag = {
  isVisible: boolean;
};

export const isComponentWithVisibleFlag = (component: unknown): component is ComponentWithVisibleFlag => {
  return isObject(component) && 'isVisible' in component && typeof component.isVisible === 'boolean';
};

export const isObject = (value: unknown): value is NonNullable<object> => typeof value === 'object' && value !== null;
